import { BaseWebComponentView } from '../../../../framework/BaseWebComponentView';
import { TemplateState } from '../../../../types/common/types';
import { Company, PackageGeneralFormViewData } from '../types/PackageGeneralFormViewData';
import { GzFormFieldWebComponent } from '../../../../ui/gzForm/formField';
import { GzInputWebComponent } from '../../../../ui/gzInput/component/GzInputWebComponent';
import { GzDropdownWebComponent } from '../../../../ui/gzDropdown';
import { LanguageData } from '../../../types/language-data';
import {
  API_TYPE_COMPANIES_FOR_GZ_FRAT_PACKAGES,
  API_TYPE_COMPANIES_FOR_GZ_PACKAGES,
} from '../../../../webcomponents.constants';
import { FormGroup } from '../../../../ui/gzForm/formGroup/form-group';
import { RequiredValidator } from '../../../../ui/gzForm/validators/required.validator';
import { MaxLengthValidator } from '../../../../ui/gzForm/validators/max-length.validator';
import { PackageConstants } from '../../../PackageConstants';
import { RemoteValidator } from '../../../../ui/gzForm/validators/remote.validator';
import { RegexValidator } from '../../../../ui/gzForm/validators/regex.validator';
import { Language } from '../../../../utils/i18n.utils';
import { GzSingleSelectComplexWebComponent } from '../../../../ui/gzSingleSelectComplex';
import { toggleVisibility } from '../../../../utils/htmlElement.utils';
import { GetItemsResponse } from '../../../../ui/gzSingleSelectComplex/types';
import { PackageData, PackagePanelType, PackageType } from '../../../packagesGrid/types';
import { OnCompanySelect } from '../types/PackageGeneralEvent';
import { translateFn } from '../../../../utils/translator.helper';
import { CustomFieldsFormConstants } from '../../../../company/common/customFieldsForm/CustomFieldsFormConstants';
import { PackageActionContext } from '../../types/PackageActionContext';

export class PackageGeneralFormView extends BaseWebComponentView {
  public formGroup!: FormGroup;

  public dom: {
    $wrapper: HTMLElement;

    $packageNameFormField: GzFormFieldWebComponent<GzInputWebComponent, string>;
    $packageDescriptionFormField: GzFormFieldWebComponent<GzInputWebComponent, string>;
    $languageFormField: GzFormFieldWebComponent<GzDropdownWebComponent, string | number>;
    $companyFormField: GzFormFieldWebComponent<GzSingleSelectComplexWebComponent, string | number>;
  };
  private packageType: PackageType;
  private company: Company;
  private packageId: string | null;
  private context: PackageActionContext;
  private panelTypeIsStaging: boolean;

  getDomElements() {
    this.dom = {
      $wrapper: this.shadow.querySelector('#package-general-form-wrapper'),

      $packageNameFormField: this.shadow.querySelector('#package-name-form-field'),
      $packageDescriptionFormField: this.shadow.querySelector('#package-description-form-field'),
      $languageFormField: this.shadow.querySelector('#language-form-field'),
      $companyFormField: this.shadow.querySelector('#company-form-field'),
    };

    this.getTranslations();
    this.setTranslatedStrings();
  }

  getTranslations() {
    this.commonStrings = this.stringsTranslator.getCommonStrings();
  }

  setTranslatedStrings() {
    this.dom.$packageNameFormField.label = translateFn('Name');
    this.dom.$packageNameFormField.Input.placeholder = translateFn('Type here');

    this.dom.$packageDescriptionFormField.label = translateFn('Description');
    this.dom.$packageDescriptionFormField.Input.placeholder = translateFn('Type here');

    this.dom.$languageFormField.label = translateFn('Language');
    this.dom.$languageFormField.Input.placeholder = translateFn('Select the language');
    this.dom.$languageFormField.Input.noResultsFound = this.commonStrings.noResultsFound;

    this.dom.$companyFormField.label = translateFn('Company');
    this.dom.$companyFormField.Input.placeholder = translateFn('Choose company');
    this.dom.$companyFormField.Input.searchPlaceholderString = translateFn('Search');
    this.dom.$companyFormField.Input.noSearchResultsString = translateFn('No options found');
  }

  buildSuccessView(viewData: PackageGeneralFormViewData) {
    this.packageType = viewData.packageType;
    this.company = viewData.company;
    this.packageId = viewData.packageId;
    this.context = viewData.context;
    this.panelTypeIsStaging = viewData.panelType === PackagePanelType.STAGING;

    this.createFormGroup();
    this.setLanguageDropdownOptions(viewData.languages);
    this.setCompanyDropdown();

    if (this.panelTypeIsStaging) {
      toggleVisibility(this.dom.$packageNameFormField, false);
      toggleVisibility(this.dom.$packageDescriptionFormField, false);
    }
  }

  buildErrorView(error: Error) {
    this.resetTemplateState(this.dom.$wrapper);
    this.setTemplateState(this.dom.$wrapper, TemplateState.ERROR);
  }

  private createFormGroup(): void {
    if (!this.panelTypeIsStaging) {
      this.setPackageNameValidators();
      this.setPackageDescriptionValidators();
      this.setCompanyValidators();
    }

    this.setPackageLanguageValidators();

    this.formGroup = FormGroup.build({
      name: this.dom.$packageNameFormField,
      description: this.dom.$packageDescriptionFormField,
      language: this.dom.$languageFormField,
      company: this.dom.$companyFormField,
    });
  }

  private setPackageNameValidators(): void {
    this.dom.$packageNameFormField.Validators = [
      RequiredValidator(this.commonStrings.requiredField),
      MaxLengthValidator(
        PackageConstants.PackageName.MaxLength,
        translateFn('The name must not be longer than {packageNameMinLength} characters in length', {
          packageNameMinLength: PackageConstants.PackageName.MaxLength,
        })
      ),
      RemoteValidator(
        (value: string) =>
          this.dependencies.api.clients.Package.CheckNameIsUnique(
            value,
            this.packageId,
            this?.packageType || null,
            this.dom.$companyFormField?.value || null
          ),
        (response: { success: boolean }) => response.success,
        translateFn('Package with this name already exists'),
        translateFn('A communication error has occurred. Please enter the package name again')
      ),
      RegexValidator(
        CustomFieldsFormConstants.RegexValueForCustomFields,
        translateFn('The name contains characters that are not allowed')
      ),
    ];
  }

  private setPackageDescriptionValidators(): void {
    this.dom.$packageDescriptionFormField.Validators = [
      MaxLengthValidator(
        PackageConstants.PackageDescription.MaxLength,
        translateFn('The description must not be longer than {packageNameMinLength} characters in length', {
          packageNameMinLength: PackageConstants.PackageDescription.MaxLength,
        })
      ),
      RegexValidator(
        CustomFieldsFormConstants.RegexValueForCustomFields,
        translateFn('The description contains characters that are not allowed')
      ),
    ];
  }

  private setPackageLanguageValidators(): void {
    this.dom.$languageFormField.Validators = [RequiredValidator(this.commonStrings.requiredField)];
  }

  private setCompanyValidators(): void {
    if (this.isCompanyAvailable()) {
      this.dom.$companyFormField.Validators = [RequiredValidator(this.commonStrings.requiredField)];
    }
  }

  private setLanguageDropdownOptions(languages: LanguageData[]): void {
    this.dom.$languageFormField.Input.options = languages.map((language: LanguageData) => {
      return {
        title: language.title,
        value: language.id,
      };
    });

    const defaultLanguage = languages.find((el: LanguageData) => el.id === Language.English);

    if (defaultLanguage) {
      this.dom.$languageFormField.Input.setFormValue(defaultLanguage.id);
    }
  }

  private setCompanyDropdown(): void {
    if (this.isCompanyAvailable()) {
      toggleVisibility(this.dom.$companyFormField, true);
      this.dom.$companyFormField.readonly = false;
      this.dom.$companyFormField.disabled = false;

      this.dom.$companyFormField.Input.apiType = this.getCompanySelectorApiBasedOnPackageType();

      const companyData: GetItemsResponse = {
        items: [],
        totalItemsCount: 0,
      };

      if (this.company) {
        companyData.items.push({
          label: this.company.name,
          value: this.company.id,
        });
        (companyData.totalItemsCount as number) += 1;
      }

      this.dom.$companyFormField.Input.updateComplete.then((): void => {
        this.dom.$companyFormField.Input.controller.setFilterOptions(companyData).then().catch();
      });
    } else {
      //if onprem or cloud(customer company) the company selector should be hidden
      // we use currentUserCompany for others checks and calls related to a company
      toggleVisibility(this.dom.$companyFormField, false);
      this.dom.$companyFormField.setAttribute('disabled', '');
    }
  }

  /**
   * This method is used when editing a company
   * - init company selector
   * - trigger company change event used in other components
   * @param packageData
   */
  public async setPackageData(packageData: PackageData) {
    this.dom.$packageNameFormField.value = packageData.name;
    this.formGroup.value = {
      name: packageData.name,
      description: packageData.description,
      language: packageData.language,
      company: packageData.company.id,
    };

    if (
      this.isCompanyAvailable() &&
      (packageData?.type === PackageType.Frat || packageData?.type === PackageType.RelayForFrat)
    ) {
      // the frat company should not be able to
      this.dom.$companyFormField.Input.readonly = true;
    }
  }

  public onCompanyChanged(companyId: string): void {
    //do not validate on customer company
    if (this.dependencies.isPartner()) {
      this.formGroup?.validate(true);
    }
    this.dom.$wrapper.dispatchEvent(new OnCompanySelect(companyId));
  }

  /**
   * Only for cloud and only for partner companies
   * @private
   */
  public isCompanyAvailable(): boolean {
    return this.dependencies.permissions.canHaveChildCompanies && this.dependencies.isCloud();
  }

  private getCompanySelectorApiBasedOnPackageType(): string {
    switch (this.packageType) {
      case PackageType.Endpoints:
        return API_TYPE_COMPANIES_FOR_GZ_PACKAGES;
      case PackageType.Frat:
        return API_TYPE_COMPANIES_FOR_GZ_FRAT_PACKAGES;
      default:
        return API_TYPE_COMPANIES_FOR_GZ_PACKAGES;
    }
  }
}
